import { sum } from '../array';

export function distribute<T, T2 extends keyof T>(
  arr: T[],
  key: T2 | ((s: T) => number),
  basePoints = 100
): [T, number][] {
  if (!arr) return [];
  if (!arr.length) return [];

  const numbers = arr.map<number | undefined>((item) => {
    let value: number | undefined;
    if (typeof item === 'number') {
      value = item;
    } else {
      if (!key)
        throw new Error('Specify a key if distribution is on an object');
      // @ts-ignore
      value = typeof key === 'function' ? key(item) : item[String(key)];
    }
    return typeof value === 'number' ? value : undefined;
  });

  const remaining = basePoints - sum(arr, (_, i) => numbers[i] || 0);
  if (remaining < 0)
    throw new Error(`Distribution exceeds maximum of ${basePoints}`);

  const noSplit = arr.filter((_, i) => numbers[i] === undefined);
  const split = remaining / noSplit.length;
  return arr.map(
    (item, i) =>
      [item, numbers[i] === undefined ? split : numbers[i]] as [T, number]
  );
}

export function distributeCent<T, T2 extends keyof T>(
  arr: T[],
  key: T2 | ((s: T) => number),
  cent: number,
  opt?: {
    oddStrategy?: 'last' | 'first' | 'random';
    basePoints?: '100_000' | '100_00' | '100';
  }
): [T, number][] {
  const oddStrategy = opt?.oddStrategy ?? 'last';
  const basePoints =
    opt?.basePoints === '100_000'
      ? 100_000
      : opt?.basePoints === '100_00'
        ? 100_00
        : 100;
  const isNegative = cent < 0;
  cent = Math.abs(cent);

  const dist = distribute(arr, key, basePoints);
  let remaining = cent;

  let firstFeasable: [T, number] = undefined as any;
  let lastFeasable: [T, number] = undefined as any;
  const result = dist.map(([item, split], _i) => {
    if (basePoints === 100_00) split = split / 100;
    if (basePoints === 100_000) split = split / 1000;

    const amount = Math.floor((cent / 100) * split);
    remaining -= amount;
    const res: [T, number] = [item, amount];
    if (amount > 0 && !firstFeasable) firstFeasable = res;
    if (amount > 0) lastFeasable = res;
    return res as [T, number];
  });
  if (!remaining) return result.map((x) => [x[0], isNegative ? -x[1] : x[1]]);

  const restDivided = Math.floor(remaining / result.length);
  let funded = 0;
  for (const x of result) {
    if (!x[1]) continue;
    x[1] += restDivided;
    funded++;
  }

  remaining = remaining - restDivided * funded;
  if (lastFeasable && oddStrategy === 'last') lastFeasable[1] += remaining;
  else if (firstFeasable && oddStrategy === 'first')
    firstFeasable[1] += remaining;
  else if (oddStrategy === 'random') {
    const random = Math.floor(Math.random() * result.length);
    result[random][1] += remaining;
  } else if (remaining > 0) {
    // When no participant has received anything yet, distribute the remaining cents
    // to the participants with the highest weights (but not 0 weights)
    const eligibleIndices = result
      .map((_, i) => i)
      .filter((i) => dist[i][1] > 0);

    if (eligibleIndices.length === 0) {
      throw new Error(
        `Could not distribute ${cent} cents to ${arr.length} targets`
      );
    }

    // Different distribution strategies when no one got initial amounts
    if (oddStrategy === 'first') {
      // Distribute to first N eligible participants
      for (let i = 0; i < remaining && i < eligibleIndices.length; i++) {
        result[eligibleIndices[i]][1] += 1;
      }
    } else {
      // Default/last: distribute to participants with highest weights
      eligibleIndices.sort((a, b) => dist[b][1] - dist[a][1]);
      for (let i = 0; i < remaining && i < eligibleIndices.length; i++) {
        result[eligibleIndices[i]][1] += 1;
      }
    }
  } else {
    throw new Error(
      `Could not distribute ${cent} cents to ${arr.length} targets`
    );
  }

  return result.map((x) => [x[0], isNegative ? -x[1] : x[1]]);
}
